/**
 * PiataProdus — pagină produs + plasare comandă (card Stripe LIVE sau comandă directă).
 * Comision platformă: 1% per tranzacție, afișat transparent.
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import {
  MapPin, Phone, CreditCard, HandCoins, Loader2, CheckCircle2,
  ShieldCheck, ArrowLeft, Minus, Plus, Store, BadgePercent,
} from 'lucide-react';
import { toast } from 'sonner';
import PiataHeader from '../components/PiataHeader';
import SiteFooter from '../components/SiteFooter';
import useSEO from '../hooks/useSEO';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { catMeta, imgUrl, fmtRON } from '../lib/piata';

export default function PiataProdus() {
  const { pid } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [mainImg, setMainImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [payMethod, setPayMethod] = useState('');
  const [buyer, setBuyer] = useState({ buyer_name: '', buyer_phone: '', buyer_email: '', buyer_address: '', note: '' });
  const [submitting, setSubmitting] = useState(false);
  const [directSuccess, setDirectSuccess] = useState(null);
  const [paidOrder, setPaidOrder] = useState(null);
  const [pollState, setPollState] = useState(null);
  const pollAttempts = useRef(0);

  useSEO({
    title: `${product?.title || 'Produs'} · Piața micilor producători · Energy Project Design`,
    description: product?.description?.slice(0, 155) || 'Produs local de la mici producători români — comision 1%, listare gratuită.',
    canonical: `https://www.energyprojectdesign.com/piata/produs/${pid}`,
  });

  useEffect(() => {
    api.get(`/piata/products/${pid}`)
      .then(({ data }) => {
        setProduct(data);
        setPayMethod(data.payment_methods?.[0] || 'direct');
      })
      .catch(() => setNotFound(true));
  }, [pid]);

  useEffect(() => {
    if (user) setBuyer((b) => ({ ...b, buyer_name: b.buyer_name || user.name || '', buyer_email: b.buyer_email || user.email || '' }));
  }, [user]);

  const pollPayment = useCallback(async (sessionId) => {
    setPollState('polling');
    try {
      const { data } = await api.get(`/piata/orders/status/${sessionId}`);
      if (data.payment_status === 'paid') {
        setPaidOrder(data.order);
        setPollState('paid');
        return;
      }
      if (pollAttempts.current < 6) {
        pollAttempts.current += 1;
        setTimeout(() => pollPayment(sessionId), 2000);
      } else {
        setPollState('timeout');
      }
    } catch (e) {
      setPollState('error');
    }
  }, []);

  useEffect(() => {
    const status = searchParams.get('status');
    const sessionId = searchParams.get('session_id');
    if (status === 'success' && sessionId) {
      pollPayment(sessionId);
    } else if (status === 'cancelled') {
      toast.error('Plata a fost anulată. Poți relua comanda oricând.');
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitOrder = async () => {
    if (!buyer.buyer_name.trim() || !buyer.buyer_phone.trim()) {
      toast.error('Numele și telefonul sunt obligatorii');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post('/piata/orders', {
        product_id: pid,
        qty,
        payment_method: payMethod,
        ...buyer,
        origin_url: window.location.origin,
      });
      if (data.payment_method === 'card' && data.url) {
        window.location.href = data.url;
        return;
      }
      setDirectSuccess(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Eroare la plasarea comenzii');
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-white">
        <PiataHeader active="piata" />
        <div className="pt-32 pb-20 text-center text-zinc-500" data-testid="piata-product-notfound">
          <div className="text-lg font-bold text-zinc-950 mb-2">Produsul nu a fost găsit</div>
          <Link to="/piata" className="text-violet-700 text-sm font-semibold hover:underline">← Înapoi la piață</Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <PiataHeader active="piata" />
        <div className="pt-32 pb-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-400" /></div>
      </div>
    );
  }

  const meta = catMeta(product.category);
  const Icon = meta.icon;
  const total = Math.round(product.price_ron * qty * 100) / 100;
  const commission = Math.round(total * 1) / 100;
  const outOfStock = (product.stock || 0) <= 0;

  // Success screens
  const successPanel = (title, lines, testid) => (
    <div className="min-h-screen bg-white" data-testid={testid}>
      <PiataHeader active="piata" />
      <div className="pt-32 pb-20 max-w-xl mx-auto px-6 text-center">
        <div className="w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="w-7 h-7 text-emerald-600" />
        </div>
        <h1 className="text-2xl font-bold text-zinc-950 font-display mb-3">{title}</h1>
        <div className="text-sm text-zinc-600 leading-relaxed space-y-2 mb-8">{lines}</div>
        <div className="flex items-center justify-center gap-3">
          <Link to="/piata" className="epd-btn text-sm py-2" data-testid="piata-success-back">Înapoi la piață</Link>
          {user && <Link to="/piata/cont" className="ghost-btn text-sm py-2 border border-zinc-300 rounded-md px-4 inline-flex items-center" data-testid="piata-success-account">Comenzile mele</Link>}
        </div>
      </div>
      <SiteFooter />
    </div>
  );

  if (paidOrder) {
    return successPanel('Plata a fost confirmată', (
      <>
        <p>Comanda <strong className="font-mono">{paidOrder.order_id}</strong> pentru <strong>{paidOrder.product_title}</strong> ({paidOrder.qty} {paidOrder.unit}) a fost plătită cu succes — <strong>{fmtRON(paidOrder.total_ron)}</strong>.</p>
        <p>Producătorul <strong>{paidOrder.seller_name}</strong> a fost notificat și te va contacta la <strong>{paidOrder.buyer_phone}</strong> pentru livrare.</p>
        <p className="text-xs text-zinc-500">Comision platformă (1%): {fmtRON(paidOrder.commission_ron)} · Net producător: {fmtRON(paidOrder.net_ron)}</p>
      </>
    ), 'piata-paid-success');
  }

  if (directSuccess) {
    return successPanel('Comanda a fost trimisă producătorului', (
      <>
        <p>Comanda <strong className="font-mono">{directSuccess.order_id}</strong> în valoare de <strong>{fmtRON(directSuccess.total_ron)}</strong> a fost înregistrată.</p>
        <p>{directSuccess.message}</p>
        {directSuccess.seller_phone && <p>Telefon producător: <strong>{directSuccess.seller_phone}</strong></p>}
      </>
    ), 'piata-direct-success');
  }

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="page-piata-produs">
      <PiataHeader active="piata" />

      {pollState === 'polling' && (
        <div className="fixed inset-0 z-[60] bg-zinc-950/70 flex items-center justify-center" data-testid="piata-payment-polling">
          <div className="bg-white rounded-md p-8 text-center max-w-sm">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-violet-700 mb-4" />
            <div className="font-bold text-zinc-950 mb-1">Verificăm plata...</div>
            <div className="text-xs text-zinc-500">Confirmăm tranzacția cu Stripe. Durează câteva secunde.</div>
          </div>
        </div>
      )}

      <div className="pt-24 pb-16 max-w-7xl mx-auto px-6 lg:px-12">
        <Link to="/piata" className="inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-500 hover:text-zinc-950 mb-6 transition-colors" data-testid="piata-back-link">
          <ArrowLeft className="w-3.5 h-3.5" /> Înapoi la piață
        </Link>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* GALERIE */}
          <div>
            <div className="border border-zinc-200 rounded-md overflow-hidden bg-zinc-50 h-[380px] flex items-center justify-center">
              {product.images?.length ? (
                <img src={imgUrl(product.images[mainImg])} alt={product.title} className="w-full h-full object-cover" data-testid="piata-product-main-image" />
              ) : (
                <Icon className="w-16 h-16 text-zinc-300" strokeWidth={1} />
              )}
            </div>
            {product.images?.length > 1 && (
              <div className="flex gap-2 mt-3">
                {product.images.map((img, i) => (
                  <button key={img} onClick={() => setMainImg(i)} className={`w-20 h-20 rounded-md overflow-hidden border-2 transition-colors ${i === mainImg ? 'border-zinc-950' : 'border-zinc-200 hover:border-zinc-400'}`} data-testid={`piata-thumb-${i}`}>
                    <img src={imgUrl(img)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Vânzător */}
            <div className="mt-6 border border-zinc-200 rounded-md p-4 flex items-start gap-3" data-testid="piata-seller-card">
              <div className="w-10 h-10 rounded-md bg-zinc-950 text-white flex items-center justify-center shrink-0">
                <Store className="w-4.5 h-4.5 w-4 h-4" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-0.5">Producător</div>
                <div className="text-sm font-bold text-zinc-950">{product.seller_name}</div>
                <div className="flex items-center gap-3 mt-1 text-xs text-zinc-600">
                  {product.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{product.location}</span>}
                  {product.seller_phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{product.seller_phone}</span>}
                </div>
              </div>
            </div>
          </div>

          {/* INFO + COMANDĂ */}
          <div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-violet-700 font-bold mb-2">
              <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {meta.label}
            </div>
            <h1 className="text-2xl lg:text-3xl font-bold tracking-[-0.03em] text-zinc-950 font-display mb-3" data-testid="piata-product-title">{product.title}</h1>
            <div className="flex items-baseline gap-2 mb-4">
              <div className="text-3xl font-bold tabular-nums text-zinc-950 font-display tracking-tight" data-testid="piata-product-price">{fmtRON(product.price_ron)}</div>
              <div className="text-sm text-zinc-500">/ {product.unit}</div>
              <div className={`ml-auto text-xs font-semibold px-2.5 py-1 rounded-full border ${outOfStock ? 'bg-zinc-100 text-zinc-500 border-zinc-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`} data-testid="piata-product-stock">
                {outOfStock ? 'Stoc epuizat' : `În stoc: ${product.stock} ${product.unit}`}
              </div>
            </div>
            {product.description && <p className="text-sm text-zinc-600 leading-relaxed mb-6 whitespace-pre-line" data-testid="piata-product-description">{product.description}</p>}

            {!outOfStock && (
              <div className="border border-zinc-200 rounded-md p-5 space-y-4" data-testid="piata-order-form">
                <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-bold">// plasează comanda</div>

                {/* Cantitate */}
                <div className="flex items-center gap-4">
                  <span className="text-xs font-semibold text-zinc-700 w-24">Cantitate</span>
                  <div className="flex items-center border border-zinc-300 rounded-md">
                    <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2 text-zinc-600 hover:text-zinc-950" data-testid="piata-qty-minus"><Minus className="w-3.5 h-3.5" /></button>
                    <span className="w-10 text-center text-sm font-bold tabular-nums" data-testid="piata-qty-value">{qty}</span>
                    <button onClick={() => setQty(Math.min(product.stock, qty + 1))} className="px-3 py-2 text-zinc-600 hover:text-zinc-950" data-testid="piata-qty-plus"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <span className="text-xs text-zinc-500">{product.unit}</span>
                </div>

                {/* Metodă plată */}
                <div>
                  <div className="text-xs font-semibold text-zinc-700 mb-2">Metoda de plată</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {product.payment_methods?.includes('card') && (
                      <button onClick={() => setPayMethod('card')} className={`flex items-start gap-2.5 border rounded-md p-3 text-left transition-all ${payMethod === 'card' ? 'border-zinc-950 bg-zinc-950 text-white' : 'border-zinc-300 hover:border-zinc-500'}`} data-testid="piata-pay-card">
                        <CreditCard className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
                        <div>
                          <div className="text-xs font-bold">Plată cu cardul</div>
                          <div className={`text-[10px] ${payMethod === 'card' ? 'text-zinc-300' : 'text-zinc-500'}`}>Securizat prin Stripe · confirmare instant</div>
                        </div>
                      </button>
                    )}
                    {product.payment_methods?.includes('direct') && (
                      <button onClick={() => setPayMethod('direct')} className={`flex items-start gap-2.5 border rounded-md p-3 text-left transition-all ${payMethod === 'direct' ? 'border-zinc-950 bg-zinc-950 text-white' : 'border-zinc-300 hover:border-zinc-500'}`} data-testid="piata-pay-direct">
                        <HandCoins className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} />
                        <div>
                          <div className="text-xs font-bold">Comandă directă</div>
                          <div className={`text-[10px] ${payMethod === 'direct' ? 'text-zinc-300' : 'text-zinc-500'}`}>Plată la livrare / transfer, direct cu producătorul</div>
                        </div>
                      </button>
                    )}
                  </div>
                </div>

                {/* Date cumpărător */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold block mb-1">Nume complet *</label>
                    <input value={buyer.buyer_name} onChange={(e) => setBuyer({ ...buyer, buyer_name: e.target.value })} className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:border-zinc-950 outline-none" data-testid="piata-buyer-name" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold block mb-1">Telefon *</label>
                    <input value={buyer.buyer_phone} onChange={(e) => setBuyer({ ...buyer, buyer_phone: e.target.value })} className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:border-zinc-950 outline-none" data-testid="piata-buyer-phone" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold block mb-1">Email</label>
                    <input value={buyer.buyer_email} onChange={(e) => setBuyer({ ...buyer, buyer_email: e.target.value })} className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:border-zinc-950 outline-none" data-testid="piata-buyer-email" />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold block mb-1">Adresă livrare</label>
                    <input value={buyer.buyer_address} onChange={(e) => setBuyer({ ...buyer, buyer_address: e.target.value })} className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:border-zinc-950 outline-none" data-testid="piata-buyer-address" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold block mb-1">Mesaj pentru producător</label>
                    <textarea value={buyer.note} onChange={(e) => setBuyer({ ...buyer, note: e.target.value })} rows={2} className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm focus:border-zinc-950 outline-none" data-testid="piata-buyer-note" />
                  </div>
                </div>

                {/* Total + comision transparent */}
                <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 text-xs space-y-1" data-testid="piata-order-summary">
                  <div className="flex justify-between"><span className="text-zinc-600">{qty} × {fmtRON(product.price_ron)}</span><span className="font-bold tabular-nums">{fmtRON(total)}</span></div>
                  <div className="flex justify-between text-zinc-500"><span className="inline-flex items-center gap-1"><BadgePercent className="w-3 h-3" /> Comision platformă (1%, inclus)</span><span className="tabular-nums">{fmtRON(commission)}</span></div>
                  <div className="flex justify-between border-t border-zinc-200 pt-1.5 text-sm"><span className="font-bold text-zinc-950">Total de plată</span><span className="font-bold tabular-nums text-zinc-950" data-testid="piata-order-total">{fmtRON(total)}</span></div>
                </div>

                <button onClick={submitOrder} disabled={submitting} className="epd-btn w-full text-sm py-3" data-testid="piata-submit-order">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : payMethod === 'card' ? <CreditCard className="w-4 h-4" /> : <HandCoins className="w-4 h-4" />}
                  {payMethod === 'card' ? `Plătește ${fmtRON(total)} cu cardul` : 'Trimite comanda producătorului'}
                </button>
                <div className="flex items-center justify-center gap-1.5 text-[10px] text-zinc-500">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                  {payMethod === 'card' ? 'Plată procesată securizat prin Stripe. Nu stocăm datele cardului.' : 'Datele tale sunt transmise doar producătorului pentru livrare.'}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <SiteFooter />
    </div>
  );
}
